import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export default function CookieConsent({ onConsentGiven }) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    try {
      const consent = sessionStorage.getItem("cookieConsent");
      if (!consent) {
        setShow(true);
      } else {
        const parsed = JSON.parse(consent);
        if (parsed && typeof parsed === "object" && "functional" in parsed) {
          onConsentGiven(parsed);
        } else {
          throw new Error("Malformed consent");
        }
      }
    } catch (err) {
      console.warn("❗ Error reading cookie consent:", err);
      sessionStorage.removeItem("cookieConsent");
      setShow(true);
    }
  }, [onConsentGiven]);

  const handleAccept = () => {
    const consentData = {
      functional: true,
      advertising: true,
    };
    sessionStorage.setItem("cookieConsent", JSON.stringify(consentData));
    setShow(false);
    onConsentGiven(consentData);
  };

  const handleReject = () => {
    const consentData = {
      functional: true,
      advertising: false,
    };
    sessionStorage.setItem("cookieConsent", JSON.stringify(consentData));
    setShow(false);
    onConsentGiven(consentData);
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black bg-opacity-60 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-title"
      aria-describedby="cookie-desc"
    >
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-xl p-6 sm:p-8 animate-fade-in">
          <h2
            id="cookie-title"
            className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 text-center sm:text-left"
          >
            {t("cookie.title")}
          </h2>

          <p
            id="cookie-desc"
            className="text-sm sm:text-base text-gray-700 mb-6"
          >
            {t("cookie.description")}
          </p>

          <div className="space-y-6">
            {/* Opcionales (primero) */}
            <div>
              <p className="font-semibold text-base sm:text-lg text-gray-900 mb-1">
                {t("cookie.ads_title")}
              </p>
              <p className="text-xs sm:text-sm text-gray-600 mb-3">
                {t("cookie.ads_desc")}
              </p>
              <div className="flex gap-4">
                <button
                  onClick={handleReject}
                  className="bg-red-600 hover:bg-red-700 text-white text-sm sm:text-base px-4 py-2 rounded-lg transition font-semibold"
                >
                  {t("cookie.reject")}
                </button>
                <button
                  onClick={handleAccept}
                  className="bg-green-600 hover:bg-green-700 text-white text-sm sm:text-base px-4 py-2 rounded-lg transition font-semibold"
                >
                  {t("cookie.accept")}
                </button>
              </div>
            </div>

            {/* Necesarias (después, siempre activas) */}
            <div>
              <p className="font-semibold text-base sm:text-lg text-gray-900 mb-1">
                {t("cookie.necessary_title")}
              </p>
              <p className="text-xs sm:text-sm text-gray-600 mb-3">
                {t("cookie.necessary_desc")}
              </p>
              <div>
                <button
                  disabled
                  className="bg-gray-400 text-white text-sm sm:text-base px-4 py-2 rounded-lg font-semibold opacity-70 cursor-not-allowed"
                >
                  {t("cookie.always_active")}
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 text-xs text-gray-500 text-center">
            {t("cookie.footer")}{" "}
            <a
              href="/politica/cookies"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-blue-600"
            >
              {t("cookie.cookies_policy")}
            </a>{" "}
            {t("cookie.and")}{" "}
            <a
              href="/politica/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-blue-600"
            >
              {t("cookie.privacy_policy")}
            </a>.
          </div>
        </div>
      </div>
    </div>
  );
}
